/**
 * 资源包管理 — frame KkJt4 @ ui/dogdisk-user.pen(2026-06-13 管理页合一)。
 *
 * 路径:`/pack/manage/:id`(owner;share-list 卡片 + ondv5「资源包设置」入口)
 * 原 M6qlR 分享详情页(/share-manage/:id)已删:分享链接复制/二维码等动作由列表 ⋮(ondv5)承载,
 * 名称/介绍/封面/价格在本页编辑(元数据面板);PATCH /gapi/user/packs/{id}。
 * 已订阅者权益不受影响;改价只影响未来订阅。
 *
 * 2026-06-13 子树模型改版(T18):包内容 = pack_folder 真实 file_info 子树。本页改为纯元数据面板;
 * 「管理包内容」入口跳 /folders/{folder_file_info_id} 复用 file-browser(上传/新建夹/删/移)直接增删改;
 * 不再在本页内嵌文件选择器,save 也不再传 items(去除基于 position 的整体替换)。
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight, Plus } from 'lucide-react'
import { Spinner, useToast, formatBytes } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { CoverPicker, type CoverPick } from '../components/modals/cover-picker'
import { PackPriceRow } from '../components/pack-price-row'
import { PackItemThumb } from '../components/pack-item-thumb'
import { PAID_MIN_HINT, updatePack, usePackDetail } from '../_lib/pack-api'
import { resourceUrl } from '../_lib/resource-node-pref'


export function PackManagePage() {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const detail = usePackDetail(id)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [cover, setCover] = useState<CoverPick | null>(null)
  const [coverDirty, setCoverDirty] = useState(false) // 区分「没动」与「清除」
  const [priceCoin, setPriceCoin] = useState<number>(PAID_MIN_HINT)
  const [showCover, setShowCover] = useState(false)
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !detail.data) return
    seeded.current = true
    setName(detail.data.name)
    setDescription(detail.data.description ?? '')
    if (detail.data.cover_file_info_id) {
      setCover({ id: detail.data.cover_file_info_id, thumbUrl: detail.data.cover_thumb_url, name: '' })
    }
    if (detail.data.price_coin != null) setPriceCoin(detail.data.price_coin)
  }, [detail.data])

  // 子树模型:包内容根 pack_folder;管理内容 = 跳文件夹页(复用 file-browser 增删改)
  const folderId = detail.data?.folder_file_info_id ?? null

  const saveMut = useMutation({
    mutationFn: () =>
      // 元数据面板:只 PATCH name/description/cover/price;内容(items)由文件夹页管理,不在此整体替换
      updatePack(id!, {
        name: name.trim(),
        // 后端语义:description 空串 = 清空
        description: description.trim(),
        price_coin: priceCoin,
        ...(coverDirty && (cover ? { cover_file_info_id: cover.id } : { clear_cover: true })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pack'] })
      toast.push('已保存')
      navigate(-1)
    },
    onError: (e) => {
      toast.push(e instanceof Error ? e.message : '保存失败', { variant: 'error' })
    },
  })

  const canSave =
    !saveMut.isPending &&
    name.trim().length > 0 &&
    priceCoin >= PAID_MIN_HINT &&
    !detail.isLoading

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="资源包管理" />

        <main data-testid="KkJt4" className="flex flex-1 flex-col gap-6 px-6 pt-3 pb-32">
          {detail.isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner className="text-gray-400" />
            </div>
          ) : detail.error || !detail.data ? (
            <p className="py-12 text-center text-[13px] text-[#FF6B6B]">
              {detail.error instanceof Error ? detail.error.message : '资源包不存在'}
            </p>
          ) : (
            <>
              {/* NameSection 资源包名称(必填;后端 validatePackName 兜底) */}
              <section className="flex flex-col gap-3">
                <h2 className="text-[15px] font-semibold text-[#1A1A1A]">资源包名称</h2>
                <input
                  data-testid="KkJt4-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="请输入资源包名称"
                  className="h-[52px] rounded-xl bg-[#F6F7F8] px-4 text-[16px] outline-none placeholder:text-[#9CA3AF]"
                />
              </section>

              {/* IntroSection 资源包介绍(≤100 字;后端兜底) */}
              <section className="flex flex-col gap-3">
                <h2 className="text-[15px] font-semibold text-[#1A1A1A]">资源包介绍</h2>
                <div className="flex flex-col gap-2 rounded-xl bg-[#F6F7F8] px-4 py-3.5">
                  <textarea
                    data-testid="KkJt4-intro-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                    placeholder="介绍一下这个资源包的内容,帮助用户更好地了解..."
                    rows={3}
                    className="min-h-[68px] resize-none bg-transparent text-[15px] leading-[1.4] outline-none placeholder:text-[#9CA3AF]"
                  />
                  <span data-testid="KkJt4-intro-counter" className="text-right text-[13px] text-[#C4C8CF]">{description.length}/100</span>
                </div>
              </section>

              {/* 封面卡(样式同创建页 RF3QP)+ 统一 CoverPicker 全网盘浏览 */}
              <section className="flex flex-col gap-3">
                <h2 className="text-[15px] font-semibold text-[#1A1A1A]">封面与标签</h2>
                <button
                  type="button"
                  data-testid="KkJt4-cover-card"
                  onClick={() => setShowCover(true)}
                  className="flex w-full items-center gap-3 rounded-xl bg-[#F6F7F8] p-3 text-left"
                >
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#E6CCFF] text-[#5B21B6]">
                    {cover?.thumbUrl ? (
                      <img src={resourceUrl(cover.thumbUrl)} alt="封面" className="h-full w-full object-cover" />
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-[#1A1A1A]">封面</span>
                    <span className="block truncate text-[11px] text-[#9CA3AF]">
                      {cover ? cover.name || '已设置封面' : '付费包建议设置封面 · 影响订阅转化'}
                    </span>
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </section>

              {/* AmountSection(T3Bk5p) → SettingsSection(nTAQE):价格上移到文件区之前 */}
              <PackPriceRow value={priceCoin} min={PAID_MIN_HINT} onChange={setPriceCoin} />

              {/* 文件列表(AEJ6m,保 T18):添加文件跳文件夹浏览器增删改;预览 5 条 + 查看全部 */}
              <section className="flex flex-col gap-3">
                <h2 className="text-[15px] font-semibold text-[#1A1A1A]">文件列表</h2>
                <button
                  type="button"
                  data-testid="KkJt4-add-file-btn"
                  disabled={!folderId}
                  onClick={() => folderId && navigate(`/folders/${folderId}`)}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] bg-white text-[16px] font-medium text-[#19caad] disabled:opacity-50"
                >
                  <Plus size={18} aria-hidden="true" />
                  添加文件
                </button>
                <div className="flex flex-col gap-2 rounded-xl bg-[#F6F7F8] p-3">
                  {detail.data.items.length > 0 ? (
                    <>
                      <button
                        type="button"
                        data-testid="KkJt4-view-all"
                        onClick={() => navigate(`/pack/manage/${id}/files`)}
                        className="flex items-center justify-between px-1 pb-2 text-[13px] font-medium text-[#19caad]"
                      >
                        查看全部文件
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                      {detail.data.items.slice(0, 5).map((it) => (
                        <div key={it.file_info_id} className="flex items-center gap-3">
                          <PackItemThumb
                            coverThumbUrl={it.cover_thumb_url}
                            type={it.type}
                            mimeType={it.mime_type}
                            name={it.name}
                            size={36}
                            iconSize={16}
                          />
                          <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-[#1A1A1A]">{it.name}</span>
                          <span className="text-[13px] text-[#9CA3AF]">{formatBytes(it.size_original ?? 0)}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="px-1 py-3 text-[13px] text-[#9CA3AF]">暂无文件,点击上方添加</p>
                  )}
                </div>
              </section>

              {name.trim().length === 0 && (
                <p className="text-[12px] text-[#FF6B6B]">请输入资源包名称</p>
              )}
            </>
          )}
        </main>

        {/* BottomActions(T1NQKl) */}
        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-[402px] flex-col gap-2 bg-white px-6 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            data-testid="KkJt4-save-btn"
            onClick={() => saveMut.mutate()}
            disabled={!canSave}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#19caad] text-[18px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            <Check size={20} aria-hidden="true" />
            {saveMut.isPending ? '保存中…' : '保存设置'}
          </button>
        </div>

        <CoverPicker
          open={showCover}
          onClose={() => setShowCover(false)}
          currentCoverId={cover?.id ?? null}
          onSelect={(pick) => {
            setCover(pick)
            setCoverDirty(true)
          }}
          onClear={() => {
            setCover(null)
            setCoverDirty(true)
          }}
        />
      </MobileScreen>
    </AppLayout>
  )
}

export default PackManagePage
